/**
 *
 * DocumentsOverviewPage
 *
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';

// resource
import { useResourceState } from 'lib/domonda-resource/react/hooks';
import { FormattedMessage } from 'lib/intl';

// ui
import { Err, ErrBoundry, Flex, Box } from '@domonda/ui';
import Pagination from '@material-ui/lab/Pagination';
import { ThemeProvider } from '@material-ui/styles';
import { decorate, Decorate } from './decorate';
import * as Icons from 'lib/domonda-icons';
import { ToastContainer } from 'react-toastify';

// modules
import {
  documentsOverviewResource,
  DocumentsOverviewActionBar,
  DocumentsOverview,
  useDocumentsOverviewQueryParams,
} from 'modules/DocumentsOverview';

import { CircularLoading } from 'modules/Loading/CircularLoading';
import { createMuiTheme } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';

import { getConfigFromStore } from 'lib/api/customerConfig';

const theme = createMuiTheme({
  palette: {
    primary: {
      light: '#757ce8',
      main: '#00aae9',
      dark: '#0593e6',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ff7961',
      main: '#f44336',
      dark: '#ba000d',
      contrastText: '#000',
    },
  },
});

export interface DocumentsOverviewPageProps {}

const DocumentsOverviewPage: React.FC<DocumentsOverviewPageProps & Decorate> = ( props ) => {
  const [sizeFactor, setSizeFactor] = useState(1);
  const [isDocumetsOpen, setIsDocumetsOpen] = useState(true)
  const { data, error } = useResourceState(documentsOverviewResource);
  const [queryParams, setQueryParams] = useDocumentsOverviewQueryParams();
  const [isColumnView, setIsColumnView] = useState(queryParams.documentState === 'New,Autoprocessed,NeedForAction,Validated,Released,Downloaded,Error,Deleted,Confirmed');
  const [isDescending, setIsDescending] = useState(false);
  const { classes } = props;
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([] as any);

  const refetchDocuments = () => {
    return documentsOverviewResource.refetch();
  }

  const columns = [0, 1, 2, 3];
  const tableHeader = useRef<HTMLTableElement>(null);
  const columnElement: any[] = [];
  columns.map(() => {
    columnElement.push(useRef(null));
  })

  const selectDocument = (containerId: string, invoiceId: string) => {
    let alreadySelected = false;
    selectedDocuments.map((el: any) => {
      if (el.ContainerId === containerId) {
        alreadySelected = true;
      }
    });
    if (alreadySelected) {
      setSelectedDocuments((prevSelected: any) => prevSelected.filter((el: any) => el.ContainerId !== containerId));
    } else {
      setSelectedDocuments((prevSelected: any) => [...prevSelected, {ContainerId: containerId, InvoiceId: invoiceId}]);
    }
  }

  const mouseDown = (index: any) => {
    setActiveIndex(index);
  }

  const mouseMove = useCallback((e) => {
    const gridColumns = columns.map((i) => {
      if (i === activeIndex) {
        const width = e.clientX - columnElement[i].current.offsetLeft + 15;
  
        if (width >= 150) { // 150 is minCellWidth
          return `${width}px`;
        }
      }

      return `${columnElement[i].current.offsetWidth}px`;
    });

    if (tableHeader.current !== null) {
      tableHeader.current.style.gridTemplateColumns = `${gridColumns.join(' ')}`;
    }

  }, [activeIndex]);

  const removeListeners = useCallback(() => {
    window.removeEventListener('mousemove', mouseMove);
    window.removeEventListener('mouseup', removeListeners);
  }, [mouseMove]);

  const mouseUp = useCallback(() => {
    setActiveIndex(null);
    removeListeners();
  }, [setActiveIndex, removeListeners]);
  
  useEffect(() => {
    if (activeIndex !== null) {
      window.addEventListener('mousemove', mouseMove);
      window.addEventListener('mouseup', mouseUp);
    }
  
    return () => {
      removeListeners();
    }
  }, [activeIndex, mouseMove, mouseUp, removeListeners]);
 
  if (error) {
    return <Err error={error} onRetry={documentsOverviewResource.retry} />;
  }

  if (!data) {
    return <CircularLoading />;
  }

  return (
    <ErrBoundry>
      <Box className={classes.container}>
        <div className={classes.sidebarMenu}>
          <div 
            className={`${classes.sidebarItem} ${classes.sidebarItemBorder}`}
            onClick={()=>setIsDocumetsOpen(!isDocumetsOpen)}
          >
            <Icons.Documents className={classes.sidebarIcon} />
            <p className={classes.sidebarItemText}><FormattedMessage id="DOCUMENTS_DOCUMENTS" /></p>
          </div>
          <div className={`${classes.sidebarSubitems} ${isDocumetsOpen ? null : classes.hideSubmenu}`}>
            <p 
              className={`${classes.submenuItem} ${queryParams.documentState === 'needForAction' ? classes.darkText : classes.lighText}`}
              onClick={() => {
                setQueryParams({ ...queryParams, PageNumber: 1, documentState: 'needForAction' });
                setIsColumnView(false);                        
            }}><FormattedMessage id="DOCUMENTS_NEED_FOR_ACTION" /></p>
            <p
              className={`${classes.submenuItem} ${queryParams.documentState === 'error' ? classes.darkText : classes.lighText}`}
              onClick={() => {
                setQueryParams({ ...queryParams, PageNumber: 1, documentState: 'error' });
                setIsColumnView(false);             
              }}
            ><FormattedMessage id="DOCUMENTS_ERROR" /></p>
            <p
              className={`${classes.submenuItem} ${queryParams.documentState === 'New,Autoprocessed,NeedForAction,Validated,Released,Downloaded,Error,Deleted,Confirmed' ? 
                classes.darkText : classes.lighText}`}
              onClick={() => {
                setQueryParams({ ...queryParams, PageNumber: 1, documentState: 'New,Autoprocessed,NeedForAction,Validated,Released,Downloaded,Error,Deleted,Confirmed' });
                setIsColumnView(true);
              }}
            ><FormattedMessage id="DOCUMENTS_ALL_DOCUMENTS" /></p>
          </div> 
          <Flex style={{display:'none'}} item>
            <Link className={classes.sidebarItem} to="/dashboard">
              <Icons.DashboardIcon className={classes.sidebarIcon} />
              Dashboard
            </Link>
          </Flex>
        </div>
        <Flex container direction="column" spacing={1} className={classes.allPages}>
          <Flex item style={{ height: 150 }}>
            <DocumentsOverviewActionBar
              sizeFactor={sizeFactor}
              onSizeFactorChange={setSizeFactor}
              isColumnView={isColumnView}
              setIsColumnView={setIsColumnView}
              selectedDocuments={selectedDocuments}
              setSelectedDocuments={setSelectedDocuments}
              data={data as any}
              refetchDocuments={refetchDocuments}
              selectDocument={selectDocument}
            />
          </Flex>
          {/*{loading && <CircularLoading />}*/}
          <Flex className={classes.documentsWrapper} item>
            {isColumnView && (
            <table className={classes.documentsTable} ref={tableHeader}>
              <thead className={classes.columnOptionsWrapper}>
                <tr>
                <th className={classes.columnMenuItem} ref={columnElement[0]}>
                  <FormattedMessage id='DOCUMENTS_CLIENT' />
                  <Icons.NavigationArrowIcon className={classes.menuArrowIcon} />
                  <span
                    className={`${classes.resizeHandle} ${activeIndex === 0 ? 'active' : 'idle'}`}
                    onMouseDown={() => mouseDown(0)}
                  >
                    <span className={classes.resizeHandleBar} />
                  </span>
                </th>
                <th className={classes.columnMenuItem} ref={columnElement[1]}
                  onClick={()=>{
                    setQueryParams({ ...queryParams, orderBy: 'TimeCreated', orderDescending: isDescending, PageNumber: 1 });
                    setIsDescending(!isDescending);
                  }}
                >
                  <FormattedMessage id='DOCUMENTS_CREATED_AT' />
                  <Icons.NavigationArrowIcon className={`${classes.menuArrowIcon} ${isDescending && classes.rotateArrowIcon}`} />
                  <span
                    className={`${classes.resizeHandle} ${activeIndex === 1 ? 'active' : 'idle'}`}
                    onMouseDown={() => mouseDown(1)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className={classes.resizeHandleBar} />
                  </span>
                </th>
                <th className={classes.columnMenuItem} ref={columnElement[2]}>
                  <FormattedMessage id='DOCUMENTS_STATE' />
                  <span
                    className={`${classes.resizeHandle} ${activeIndex === 2 ? 'active' : 'idle'}`}
                    onMouseDown={() => mouseDown(2)}
                  >
                    <span className={classes.resizeHandleBar} />
                  </span>
                </th>
                <th className={classes.columnMenuItem} ref={columnElement[3]}>
                  Container ID
                  <span
                    className={`${classes.resizeHandle} ${activeIndex === 3 ? 'active' : 'idle'}`}
                    onMouseDown={() => mouseDown(3)}
                  >
                    <span className={classes.resizeHandleBar} />
                  </span>
                </th>
                </tr>
              </thead>
              <DocumentsOverview
                documents={data as any}
                sizeFactor={sizeFactor}
                isColumnView={true}
                selectDocument={selectDocument}
                selectedDocuments={selectedDocuments}
                canSelectDocuments={false}
              />
            </table>
            )}
            {!isColumnView && (
              <DocumentsOverview
                documents={data as any}
                sizeFactor={sizeFactor}
                isColumnView={false}
                selectDocument={selectDocument}
                selectedDocuments={selectedDocuments}
                canSelectDocuments={getConfigFromStore().CanDeleteInvoiceInValidation && queryParams.documentState === 'error' ? true : false}
              />
            )}
          </Flex> 
          <Flex style={{ margin: '25px 0 50px' }} alignSelf="center" item>
            <ThemeProvider theme={theme}>
              <Pagination
                page={queryParams.PageNumber ? queryParams.PageNumber : undefined}
                onChange={(_event: object, page: number) => {
                  setQueryParams({ ...queryParams, PageNumber: page });
                  setSelectedDocuments([]);
                }}
                size="large"
                showFirstButton
                showLastButton
                count={data.TotalPageCount}
                color="primary"
                className={classes.pagination}
              />
            </ThemeProvider>
          </Flex>
        </Flex>
      </Box>
      <ToastContainer />
    </ErrBoundry>
  );
};

const StyledDocumentsOverview = decorate(DocumentsOverviewPage);
export { StyledDocumentsOverview as DocumentsOverviewPage };
